import {
  Group,
  Paper,
  Portal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales/index';
import {
  type CSSProperties,
  type KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isEmpty, uniqueBy } from 'remeda';
import type { TemplateVariableCategory, TemplateVariableItem } from './use-template-variables';

type VariableSuggestionListProps = {
  categories: TemplateVariableCategory[];
  command: (item: TemplateVariableItem) => void;
  onClose?: () => void;
  initialCategoryLabel?: string | null;
};

export type SuggestionPopupAnchor = Pick<DOMRect, 'left' | 'top' | 'bottom'>;

type VariableSuggestionPopupProps = VariableSuggestionListProps & {
  anchor: SuggestionPopupAnchor;
};

export const VARIABLE_SLASH_TRIGGER = /(^|\s)\/$/;

const VARIABLE_SUGGESTION_LIST_WIDTH = 280;

const VariableSuggestionList = ({
  categories,
  command,
  onClose,
  initialCategoryLabel,
}: VariableSuggestionListProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [activeCategoryLabel, setActiveCategoryLabel] = useState<string | null>(
    initialCategoryLabel ?? null,
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const isSearching = !isEmpty(query);
  const activeCategory =
    categories.find(category => category.label === activeCategoryLabel) ?? null;

  const allVariables = useMemo(
    () =>
      uniqueBy(
        categories.flatMap(category => category.variables),
        ({ value }) => value,
      ),
    [categories],
  );

  const shownVariables = isSearching
    ? filterVariables(allVariables, query)
    : (activeCategory?.variables ?? null);
  const navLength = shownVariables ? shownVariables.length : categories.length;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the highlighted item whenever the shown list changes
  useEffect(() => setSelectedIndex(0), [query, activeCategoryLabel]);

  const selectAt = (index: number) => {
    if (shownVariables) {
      const item = shownVariables[index];
      if (item) command(item);
      return;
    }
    const category = categories[index];
    if (category) setActiveCategoryLabel(category.label);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (!isSearching && activeCategory) {
        setActiveCategoryLabel(null);
        return;
      }
      onClose?.();
      return;
    }
    if (event.key === 'Backspace' && isEmpty(query)) {
      event.preventDefault();
      onClose?.();
      return;
    }
    if (navLength === 0) return;

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex(index => (index + navLength - 1) % navLength);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex(index => (index + 1) % navLength);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      selectAt(selectedIndex);
    }
  };

  const renderVariables = (items: TemplateVariableItem[]) => (
    <ScrollArea.Autosize mah={280} type="scroll">
      <Stack gap={2}>
        {items.map((item, index) => (
          <UnstyledButton
            key={item.value}
            onClick={() => command(item)}
            onMouseEnter={() => setSelectedIndex(index)}
            px="xs"
            py={4}
            bg={index === selectedIndex ? 'purple.0' : undefined}
            style={{ borderRadius: 6 }}
          >
            <Text size="sm">{item.description}</Text>
            <Text size="xs" c="dimmed">{`{{${item.value}}}`}</Text>
          </UnstyledButton>
        ))}
      </Stack>
    </ScrollArea.Autosize>
  );

  return (
    <Paper withBorder shadow="md" radius="md" p={4} w={VARIABLE_SUGGESTION_LIST_WIDTH}>
      <TextInput
        ref={inputRef}
        size="xs"
        mb={4}
        placeholder={t(i18nKeys.FORM.SEARCH)}
        value={query}
        onChange={event => setQuery(event.currentTarget.value)}
        onKeyDown={handleKeyDown}
      />

      {isSearching ? (
        isEmpty(shownVariables ?? []) ? (
          <Text size="sm" c="dimmed" px="xs" py={4}>
            {t(i18nKeys.NO_RESULT_FOR_THIS_SEARCH)}
          </Text>
        ) : (
          renderVariables(shownVariables ?? [])
        )
      ) : activeCategory ? (
        <>
          <UnstyledButton
            onClick={() => setActiveCategoryLabel(null)}
            px="xs"
            py={4}
            style={{ borderRadius: 6 }}
          >
            <Group gap={4} wrap="nowrap">
              <IconChevronLeft size={14} />
              <Text size="xs" fw={600}>
                {activeCategory.label}
              </Text>
            </Group>
          </UnstyledButton>
          {renderVariables(activeCategory.variables)}
        </>
      ) : (
        <ScrollArea.Autosize mah={280} type="scroll">
          <Stack gap={2}>
            {categories.map((category, index) => (
              <UnstyledButton
                key={category.label}
                onClick={() => setActiveCategoryLabel(category.label)}
                onMouseEnter={() => setSelectedIndex(index)}
                px="xs"
                py={4}
                bg={index === selectedIndex ? 'purple.0' : undefined}
                style={{ borderRadius: 6 }}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Text size="sm">{category.label}</Text>
                  <Group gap={4} wrap="nowrap">
                    <Text size="xs" c="dimmed">
                      {category.variables.length}
                    </Text>
                    <IconChevronRight size={14} />
                  </Group>
                </Group>
              </UnstyledButton>
            ))}
          </Stack>
        </ScrollArea.Autosize>
      )}
    </Paper>
  );
};

export const VariableSuggestionPopup = ({ anchor, ...listProps }: VariableSuggestionPopupProps) => (
  <Portal>
    <div style={getSuggestionPopupStyle(anchor)}>
      <VariableSuggestionList key={listProps.initialCategoryLabel} {...listProps} />
    </div>
  </Portal>
);

const SUGGESTION_POPUP_MAX_HEIGHT = 340;

const POPUP_VIEWPORT_MARGIN = 8;

const getSuggestionPopupStyle = (anchor: SuggestionPopupAnchor): CSSProperties => {
  const spaceBelow = window.innerHeight - anchor.bottom;
  const shouldFlipUp = spaceBelow < SUGGESTION_POPUP_MAX_HEIGHT && anchor.top > spaceBelow;

  return {
    position: 'fixed',
    left: Math.min(
      anchor.left,
      window.innerWidth - VARIABLE_SUGGESTION_LIST_WIDTH - POPUP_VIEWPORT_MARGIN,
    ),
    zIndex: 300,
    ...(shouldFlipUp
      ? { bottom: window.innerHeight - anchor.top + 4 }
      : { top: anchor.bottom + 4 }),
  };
};

const filterVariables = (
  variables: TemplateVariableItem[],
  query: string,
): TemplateVariableItem[] => {
  const normalizedQuery = query.toLowerCase();

  return variables
    .filter(
      ({ value, description }) =>
        value.toLowerCase().includes(normalizedQuery) ||
        description.toLowerCase().includes(normalizedQuery),
    )
    .slice(0, 20);
};
